import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';
import { isCacheValid, getCacheKey } from '../cacheUtils';

export const fetchEventGroups = createAsyncThunk(
  'events/fetchGroups',
  async (search = '', { getState, rejectWithValue }) => {
    const state = getState().events;
    const cacheKey = `groups_${search}`;
    if (isCacheValid(state.lastFetched[cacheKey])) {
      return { data: state.groups[cacheKey], cacheKey, cached: true };
    }
    try {
      const response = await Axios.get(`/api/event?search=${search}&group=name`);
      return { data: response.data.data, cacheKey, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch event groups');
    }
  }
);

export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async ({ search = '', offset = 0, limit = 25, name, user } = {}, { getState, rejectWithValue }) => {
    const cacheKey = getCacheKey({ search, offset, limit, name, user });
    const state = getState().events;
    if (isCacheValid(state.lastFetched[cacheKey])) {
      return { data: state.data[cacheKey], cacheKey, cached: true };
    }
    try {
      const computedOffset = offset !== 0 ? offset / 25 : 0;
      const url = `/api/event?search=${search}&offset=${computedOffset}&limit=${limit}&name=${name}${user ? `&user=${user}` : ''}`;
      const response = await Axios.get(url);
      return { data: response.data.data, cacheKey, cached: false };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch events');
    }
  }
);

const eventsSlice = createSlice({
  name: 'events',
  initialState: {
    data: {},
    groups: {},
    loading: false,
    groupsLoading: false,
    error: null,
    lastFetched: {},
  },
  reducers: {
    invalidateCache: (state) => {
      state.lastFetched = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventGroups.pending, (state, action) => {
        const cacheKey = `groups_${action.meta.arg || ''}`;
        if (!state.groups[cacheKey]) state.groupsLoading = true;
      })
      .addCase(fetchEventGroups.fulfilled, (state, action) => {
        const { data, cacheKey, cached } = action.payload;
        if (!cached) {
          state.groups[cacheKey] = data;
          state.lastFetched[cacheKey] = Date.now();
        }
        state.groupsLoading = false;
      })
      .addCase(fetchEventGroups.rejected, (state, action) => {
        state.groupsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchEvents.pending, (state, action) => {
        const cacheKey = getCacheKey(action.meta.arg);
        if (!state.data[cacheKey]) state.loading = true;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        const { data, cacheKey, cached } = action.payload;
        if (!cached) {
          state.data[cacheKey] = data;
          state.lastFetched[cacheKey] = Date.now();
        }
        state.loading = false;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { invalidateCache: invalidateEventsCache } = eventsSlice.actions;
export default eventsSlice.reducer;